import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  IconButton,
  Skeleton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Outlet } from "@tanstack/react-router";
import * as React from "react";
import { useState } from "react";
import { useDocumentTitle, useLocalStorage } from "usehooks-ts";
import { useAuthenticated, useHasAnyPermission } from "../../hooks/authHooks";
import { useActiveLink } from "../../hooks/routeHooks";
import { HotkeyPaletteIcon } from "../../hotkeyPalette";
import { NoPermission } from "./noPermission";

const GlobalSearch = React.lazy(() => import("../globalSearch/globalSearch"));
const DarkModeSelector = React.lazy(() => import("./darkModeSelector"));
const ApplicationStatus = React.lazy(() => import("../applicationStatus"));
const InformationButton = React.lazy(() => import("./informationButton"));
const Menu = React.lazy(() => import("../menu"));

export const MainLayout: React.FC = () => {
  const [menuOpenDesktop, setMenuOpenDesktop] = useLocalStorage(
    "menu-open",
    true,
  );
  const [menuOpenMobile, setMenuOpenMobile] = useState(false);
  const authenticated = useAuthenticated();
  const activeLink = useActiveLink();
  const hasPermission = useHasAnyPermission();
  const hasViewPermission =
    !activeLink?.permissions || hasPermission(activeLink.permissions);
  useDocumentTitle(activeLink ? `${activeLink.label} - Digero` : "Digero");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const menuOpen = isMobile ? menuOpenMobile : menuOpenDesktop;
  const handleMenuToggle = () => {
    if (isMobile) {
      setMenuOpenMobile(!menuOpenMobile);
    } else {
      setMenuOpenDesktop(!menuOpenDesktop);
    }
  };

  const renderMainContent = () => {
    if (!authenticated) return <Skeleton />;
    if (!hasViewPermission) return <NoPermission />;
    return <Outlet />;
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        flexDirection: "column",
      }}
    >
      <AppBar
        position="sticky"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle menu"
            onClick={handleMenuToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography sx={{ flexGrow: 1 }}>
            {activeLink?.label}
            <InformationButton />
            <HotkeyPaletteIcon />
          </Typography>
          <GlobalSearch />
          {!isMobile && <DarkModeSelector />}
          <ApplicationStatus />
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Menu
          open={menuOpen}
          handleClose={handleMenuToggle}
          handleOpen={handleMenuToggle}
        />
        <Box component="main" sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {renderMainContent()}
        </Box>
      </Box>
    </Box>
  );
};
